/**
 * Reserved awareness shape for presence (Plan v3-01, todo `presence`).
 *
 * The collab pod enforces the reserved namespaces (`presence.*`,
 * `comment.*`, `webrtc.*`, top-level `user`) via
 * `apps/collab/awareness-guard.ts` — widget-origin payloads in these
 * keys are dropped. We keep the types here as the single source of
 * truth for both publishers (this folder) and the future SDK-side
 * mirror.
 */

export interface PresenceUser {
  /** Stable portal user id; the only field other peers can rely on for identity. */
  userId: string;
  username: string;
  /** Hex color, e.g. `#3b82f6`. Hashed from userId for deterministic per-user assignment. */
  color: string;
  avatarUrl?: string;
  /** Hocuspocus client id stringified — useful for keying remote-cursor React nodes. */
  peerId: string;
}

export interface PresenceCursor {
  /** Canvas-space x. ReactFlow uses screen-space — convert via `useReactFlow().screenToFlowPosition`. */
  x: number;
  y: number;
  /** ms epoch — peers stop rendering remote cursors when `Date.now() - ts > STALE_AFTER_MS`. */
  ts: number;
}

export interface PresenceViewport {
  /** Canvas-space rectangle the remote user is currently looking at. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PresenceFollow {
  /** peerId being followed; null when the local user is leading. */
  followingPeerId: string | null;
}

export interface PresenceFocus {
  /**
   * The workspace item (app) the peer is currently focused on —
   * selected or maximized. `null` when they're not in any app. Lets
   * other tabs draw a "so-and-so is in this app" ring around it.
   */
  itemId: string | null;
}

export interface PresenceScreen {
  /**
   * The screen (app window / board, window-management v2) the peer's
   * camera is parked on — their `currentScreenId`. Coarser and stabler
   * than `focus`: it changes on navigation, not selection. Drives the
   * peer avatars on taskbar entries and overview covers.
   */
  itemId: string | null;
}

/**
 * Aggregate published under reserved awareness key `presence`.
 * Splitting into a single map (rather than `presence.cursor`,
 * `presence.viewport` separately) keeps a single observe() callback
 * on the consumer side; payload size is bounded by the small fields.
 */
export interface PresencePayload {
  cursor?: PresenceCursor;
  viewport?: PresenceViewport;
  follow?: PresenceFollow;
  focus?: PresenceFocus;
  screen?: PresenceScreen;
}

/** A remote participant: stable identity + where they are (focused app,
 *  parked screen). */
export interface Participant {
  user: PresenceUser;
  focusItemId: string | null;
  screenItemId: string | null;
}

export const PRESENCE_AWARENESS_KEY = 'presence' as const;
/** Remote cursors older than this drop from render. */
export const PRESENCE_STALE_AFTER_MS = 5_000;
/** Cursor emit rate cap (30Hz). */
export const PRESENCE_CURSOR_EMIT_INTERVAL_MS = 33;
